import axios from 'axios';
import { clearAuthStorage, getStoredItem, setStoredItem } from './authStorage';

// Use relative URL so API calls go through the same nginx that serves the frontend
const API_BASE_URL = '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = getStoredItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = getStoredItem('refresh_token');
      if (refreshToken && !originalRequest.url?.includes('/auth/')) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          });
        }
        originalRequest._retry = true;
        isRefreshing = true;
        try {
          const res = await api.post('/auth/refresh', { refresh_token: refreshToken });
          const { access_token, refresh_token: newRefresh } = res.data;
          setStoredItem('access_token', access_token);
          if (newRefresh) setStoredItem('refresh_token', newRefresh);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          processQueue(null, access_token);
          return api(originalRequest);
        } catch (refreshErr) {
          processQueue(refreshErr, null);
          clearAuthStorage();
          window.location.href = '/login';
          return Promise.reject(refreshErr);
        } finally {
          isRefreshing = false;
        }
      }
      // Auth endpoint 401 (login/refresh failure) — just propagate the error
      // so the Login component can display it, no redirect needed
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }
      // Non-auth endpoint 401 with no refresh token — clear session and redirect
      clearAuthStorage();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  adminLogin: (email, password) => 
    api.post('/auth/admin/login', { email, password }),
  
  clientLogin: (shop_name, email, password) => 
    api.post('/auth/client/login', { shop_name, email, password }),

  staffLogin: (shop_name, email, password) =>
    api.post('/auth/staff/login', { shop_name, email, password }),

  searchShops: (q) =>
    api.get('/auth/shop/search', { params: { q } }),
  
  refreshToken: (refreshToken) => 
    api.post('/auth/refresh', { refresh_token: refreshToken }),

  verifyPassword: (password) =>
    api.post('/auth/verify-password', { password }),

  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
};

// Customer API
export const customerAPI = {
  list: (params) => 
    api.get('/customers', { params }),
  
  get: (id) => 
    api.get(`/customers/${id}`),
  
  create: (data, force = false) => 
    api.post(`/customers${force ? '?force=true' : ''}`, data),
  
  update: (id, data, force = false) => 
    api.put(`/customers/${id}${force ? '?force=true' : ''}`, data),
  
  delete: (id) => 
    api.delete(`/customers/${id}`),
  
  importVCF: (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const qp = new URLSearchParams();
    if (params.keywords) qp.set('keywords', params.keywords);
    if (params.dedup !== undefined) qp.set('dedup', params.dedup);
    if (params.rebuild !== undefined) qp.set('rebuild', params.rebuild);
    if (params.import_type) qp.set('import_type', params.import_type);
    const qs = qp.toString();
    return api.post(`/customers/import/vcf${qs ? '?' + qs : ''}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  stats: () => 
    api.get('/customers/stats'),

  invoices: (id) =>
    api.get(`/customers/${id}/invoices`),

  outstanding: () =>
    api.get('/customers/outstanding'),
};

// Supplier API
export const supplierAPI = {
  list: (params) => api.get('/suppliers', { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  outstanding: () => api.get('/suppliers/outstanding'),
  listBills: (supplierId, params) => api.get(`/suppliers/${supplierId}/bills`, { params }),
  listAllBills: (params) => api.get('/suppliers/bills/all', { params }),
  createBill: (supplierId, data) => api.post(`/suppliers/${supplierId}/bills`, data),
  updateBill: (billId, data) => api.put(`/suppliers/bills/${billId}`, data),
  deleteBill: (billId) => api.delete(`/suppliers/bills/${billId}`),
  scanInvoice: (formData) => api.post('/suppliers/scan-invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
};

// Product API
export const productAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  stats: (params) => api.get('/products/stats', { params }),
  suppliersWithProducts: () => api.get('/products/suppliers-with-products'),
  adjustStock: (id, adjustment, notes) =>
    api.post(`/products/${id}/adjust-stock`, { adjustment, notes }),
  getRegister: (id) => api.get(`/products/${id}/register`),
  // Item Categories (hierarchical)
  listCategories: () => api.get('/products/item-categories/list'),
  createCategory: (data) => api.post('/products/item-categories', data),
  updateCategory: (id, data) => api.put(`/products/item-categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/products/item-categories/${id}`),
  // Item Variants
  listVariants: (productId) => api.get(`/products/${productId}/variants`),
  createVariant: (productId, data) => api.post(`/products/${productId}/variants`, data),
  updateVariant: (productId, variantId, data) => api.put(`/products/${productId}/variants/${variantId}`, data),
  deleteVariant: (productId, variantId) => api.delete(`/products/${productId}/variants/${variantId}`),
  // Item Suppliers (multi-supplier mapping)
  listItemSuppliers: (productId) => api.get(`/products/${productId}/suppliers`),
  addItemSupplier: (productId, data) => api.post(`/products/${productId}/suppliers`, data),
  removeItemSupplier: (productId, linkId) => api.delete(`/products/${productId}/suppliers/${linkId}`),
  // Bundle / Combo items
  listBundleItems: (productId) => api.get(`/products/${productId}/bundle-items`),
  addBundleItem: (productId, data) => api.post(`/products/${productId}/bundle-items`, data),
  removeBundleItem: (productId, itemId) => api.delete(`/products/${productId}/bundle-items/${itemId}`),
};

// Invoice API
export const invoiceAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.patch(`/invoices/${id}`, data),
  fullUpdate: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  stats: () => api.get('/invoices/stats'),
  pdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  uploadPhoto: (id, formData) => api.post(`/invoices/${id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deletePhoto: (id, photoUrl) => api.delete(`/invoices/${id}/photos`, { params: { photo_url: photoUrl } }),
  scanSerial: (formData) => api.post('/invoices/scan-serial', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }),
};

// Quotation API (reuses invoice endpoints with bill_type filter)
export const quotationAPI = {
  list: (params) => api.get('/invoices', { params: { ...params, bill_type: 'quotation' } }),
  listProforma: (params) => api.get('/invoices', { params: { ...params, bill_type: 'proforma' } }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.patch(`/invoices/${id}`, data),
  fullUpdate: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  listDummyShops: () => api.get('/settings/dummy-shops'),
  saveDummyShop: (slot, data) => api.put(`/settings/dummy-shops/${slot}`, data),
  deleteDummyShop: (slot) => api.delete(`/settings/dummy-shops/${slot}`),
  uploadDummyShopLogo: (slot, formData) => api.post(`/settings/dummy-shops/${slot}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.patch('/settings', data),
  uploadLogo: (formData) => api.post('/settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadSignature: (formData) => api.post('/settings/signature', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendMessage: (data) => {
    if (data instanceof FormData) {
      return api.post('/settings/send-message', data, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 });
    }
    return api.post('/settings/send-message', data);
  },
  sendInvoice: (data) => api.post('/settings/send-invoice', data),
  sendLedger: (data) => api.post('/settings/send-ledger', data),
  marketingContacts: (tab, search) => api.get('/settings/marketing-contacts', { params: { tab, search } }),
  promoteMarketingContacts: (contact_ids, target) => api.post('/settings/marketing-contacts/promote', { contact_ids, target }),
  bulkCategoryMarketingContacts: (contact_refs, category) => api.post('/settings/marketing-contacts/bulk-category', { contact_refs, category }),
  updateChannelReadiness: (updates) => api.post('/settings/marketing-contacts/channel-readiness', { updates }),
  // Google Drive Backup
  gdriveAuthUrl: () => api.get('/settings/gdrive/auth-url'),
  gdriveExchangeCode: (code) => api.post('/settings/gdrive/exchange', { code }),
  gdriveStatus: () => api.get('/settings/gdrive/status'),
  gdriveBackupNow: () => api.post('/settings/gdrive/backup-now'),
  gdriveDisconnect: () => api.delete('/settings/gdrive/disconnect'),
  gdriveUpdateConfig: (data) => api.patch('/settings/gdrive/config', data),
  // Local Backup & Restore
  listBackups: () => api.get('/settings/backup/list'),
  downloadBackupNow: () => api.post('/settings/backup/download-now', {}, { responseType: 'blob' }),
  downloadBackupFile: (filename) => api.get(`/settings/backup/download/${filename}`, { responseType: 'blob' }),
  restoreBackup: (formData) => api.post('/settings/backup/restore', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }),
};

// WhatsApp Web API
export const whatsappAPI = {
  status: () => api.get('/whatsapp/status'),
  send: (formData) => api.post('/whatsapp/send', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  logout: () => api.post('/whatsapp/logout'),
  sentToday: (hours = 24) => api.get(`/whatsapp/sent-today?hours=${hours}`),
};

// Admin API
export const adminAPI = {
  listClients: (params) => api.get('/admin/clients', { params }),
  getClient: (id) => api.get(`/admin/clients/${id}`),
  createClient: (data) => api.post('/admin/clients', data),
  updateClient: (id, data) => api.put(`/admin/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/admin/clients/${id}`),
  resetClientPassword: (id, new_password) => api.post(`/admin/clients/${id}/reset-password`, { new_password }),
  updateMaxStaff: (id, max_staff) => api.put(`/admin/clients/${id}/max-staff`, { max_staff }),
  listClientStaff: (id) => api.get(`/admin/clients/${id}/staff`),
  resetStaffPassword: (clientId, staffId, new_password) =>
    api.post(`/admin/clients/${clientId}/staff/${staffId}/reset-password`, { new_password }),
};

// Staff API (for shop owners)
export const staffAPI = {
  list: () => api.get('/staff'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  resetPassword: (id, new_password) => api.post(`/staff/${id}/reset-password`, { new_password }),
  delete: (id) => api.delete(`/staff/${id}`),
};

// Bulk import/export API
export const bulkAPI = {
  exportCustomers: () => api.get('/bulk/customers/export', { responseType: 'blob' }),
  importCustomers: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/bulk/customers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  customerTemplate: () => api.get('/bulk/customers/template', { responseType: 'blob' }),
  exportProducts: () => api.get('/bulk/products/export', { responseType: 'blob' }),
  importProducts: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/bulk/products/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  productTemplate: () => api.get('/bulk/products/template', { responseType: 'blob' }),
  exportSuppliers: () => api.get('/bulk/suppliers/export', { responseType: 'blob' }),
  importSuppliers: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/bulk/suppliers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  supplierTemplate: () => api.get('/bulk/suppliers/template', { responseType: 'blob' }),
};

// Report API
export const reportAPI = {
  profitLoss: (params) => api.get('/reports/profit-loss', { params }),
  gst: (params) => api.get('/reports/gst', { params }),
  stock: (params) => api.get('/reports/stock', { params }),
  customerReport: (params) => api.get('/reports/customers', { params }),
  productReport: (params) => api.get('/reports/products', { params }),
  brandReport: (params) => api.get('/reports/brands', { params }),
  categoryReport: (params) => api.get('/reports/categories', { params }),
  caReport: (params) => api.get('/reports/ca-summary', { params }),
  salesReport: (params) => api.get('/reports/sales', { params }),
  purchasesReport: (params) => api.get('/reports/purchases', { params }),
  dashboardSummary: () => api.get('/reports/dashboard-summary'),
  exportExcel: (reportType, params) => api.get(`/reports/${reportType}/export-excel`, { params, responseType: 'blob' }),
  exportPdf: (reportType, params) => api.get(`/reports/${reportType}/export-pdf`, { params, responseType: 'blob' }),
};

// Expense API
export const expenseAPI = {
  list: (params) => api.get('/expenses', { params }),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  stats: (params) => api.get('/expenses/stats', { params }),
  categories: () => api.get('/expenses/categories'),
};

// Activity Log API
export const activityAPI = {
  list: (params) => api.get('/activity', { params }),
};

// Payment Records API
export const paymentAPI = {
  record: (data) => api.post('/payments', data),
  recordDirect: (data) => api.post('/payments/direct', data),
  updateDirect: (id, data) => api.patch(`/payments/direct/${id}`, data),
  getForInvoice: (invoiceId) => api.get(`/payments/invoice/${invoiceId}`),
  delete: (id) => api.delete(`/payments/${id}`),
  getLedger: (partyType, partyId) => api.get(`/payments/ledger/${partyType}/${partyId}`),
  summary: () => api.get('/payments/summary'),
  reminders: () => api.get('/payments/reminders'),
  dismissReminder: (id) => api.patch(`/payments/reminders/${id}/dismiss`),
  getStockNotifications: () => api.get('/invoices/stock-notifications/pending'),
  updateStockNotification: (id, action) => api.patch(`/invoices/stock-notifications/${id}?action=${action}`),
};

// Credit/Debit Notes API
export const cdNoteAPI = {
  list: (params) => api.get('/credit-debit-notes', { params }),
  get: (id) => api.get(`/credit-debit-notes/${id}`),
  create: (data) => api.post('/credit-debit-notes', data),
  delete: (id) => api.delete(`/credit-debit-notes/${id}`),
};

// GST Reports API
export const gstAPI = {
  gstr1: (params) => api.get('/gst/gstr1', { params }),
  gstr3b: (params) => api.get('/gst/gstr3b', { params }),
  hsnSummary: (params) => api.get('/gst/hsn-summary', { params }),
};

// Accounting API
export const accountingAPI = {
  initializeAccounts: () => api.post('/accounting/accounts/initialize'),
  listAccounts: (params) => api.get('/accounting/accounts', { params }),
  createAccount: (data) => api.post('/accounting/accounts', data),
  updateAccount: (id, data) => api.patch(`/accounting/accounts/${id}`, data),
  createJournalEntry: (data) => api.post('/accounting/journal-entries', data),
  listJournalEntries: (params) => api.get('/accounting/journal-entries', { params }),
  trialBalance: (params) => api.get('/accounting/trial-balance', { params }),
  balanceSheet: (params) => api.get('/accounting/balance-sheet', { params }),
  cashFlow: (params) => api.get('/accounting/cash-flow', { params }),
  listBankTransactions: (params) => api.get('/accounting/bank-transactions', { params }),
  createBankTransaction: (data) => api.post('/accounting/bank-transactions', data),
  reconcile: (id) => api.post(`/accounting/bank-transactions/${id}/reconcile`),
};

// Warehouse API
export const warehouseAPI = {
  list: () => api.get('/warehouse/warehouses'),
  create: (data) => api.post('/warehouse/warehouses', data),
  update: (id, data) => api.patch(`/warehouse/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouse/warehouses/${id}`),
  stock: (id, params) => api.get(`/warehouse/warehouses/${id}/stock`, { params }),
  createTransfer: (data) => api.post('/warehouse/stock-transfers', data),
  listTransfers: (params) => api.get('/warehouse/stock-transfers', { params }),
  completeTransfer: (id) => api.post(`/warehouse/stock-transfers/${id}/complete`),
  generateBarcode: (productId) => api.post(`/warehouse/generate-barcode/${productId}`),
  bulkBarcodes: () => api.post('/warehouse/generate-barcodes-bulk'),
};

// Stock Audit API
export const stockAuditAPI = {
  list: (params) => api.get('/stock-audits', { params }),
  get: (id) => api.get(`/stock-audits/${id}`),
  create: (data) => api.post('/stock-audits', data),
  reconcile: (id, data) => api.post(`/stock-audits/${id}/reconcile`, data),
  delete: (id) => api.delete(`/stock-audits/${id}`),
};

// CRM API
export const crmAPI = {
  listLeads: (params) => api.get('/crm/leads', { params }),
  getLead: (id) => api.get(`/crm/leads/${id}`),
  createLead: (data) => api.post('/crm/leads', data),
  updateLead: (id, data) => api.patch(`/crm/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/crm/leads/${id}`),
  convertLead: (id) => api.post(`/crm/leads/${id}/convert`),
  pipelineStats: () => api.get('/crm/pipeline'),
  listFollowUps: (params) => api.get('/crm/follow-ups', { params }),
  createFollowUp: (data) => api.post('/crm/follow-ups', data),
  completeFollowUp: (id) => api.post(`/crm/follow-ups/${id}/complete`),
  deleteFollowUp: (id) => api.delete(`/crm/follow-ups/${id}`),
  listLoyaltyPrograms: () => api.get('/crm/loyalty'),
  createLoyaltyProgram: (data) => api.post('/crm/loyalty', data),
  toggleLoyaltyProgram: (id) => api.patch(`/crm/loyalty/${id}/toggle`),
  listDiscountRules: () => api.get('/crm/discount-rules'),
  createDiscountRule: (data) => api.post('/crm/discount-rules', data),
  deleteDiscountRule: (id) => api.delete(`/crm/discount-rules/${id}`),
  customerSegments: () => api.get('/crm/customer-segments'),
};

// Branch API
export const branchAPI = {
  list: () => api.get('/branches'),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.patch(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
  summary: (id) => api.get(`/branches/${id}/summary`),
};

// HR & Payroll API
export const hrAPI = {
  listEmployees: (params) => api.get('/hr/employees', { params }),
  getEmployee: (id) => api.get(`/hr/employees/${id}`),
  createEmployee: (data) => api.post('/hr/employees', data),
  updateEmployee: (id, data) => api.patch(`/hr/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/hr/employees/${id}`),
  createSalary: (data) => api.post('/hr/salary', data),
  listSalaries: (params) => api.get('/hr/salary', { params }),
  approveSalary: (id) => api.post(`/hr/salary/${id}/approve`),
  paySalary: (id) => api.post(`/hr/salary/${id}/pay`),
  bulkGenerateSalary: (data) => api.post('/hr/salary/bulk-generate', data),
  markAttendance: (data) => api.post('/hr/attendance', data),
  bulkAttendance: (data) => api.post('/hr/attendance/bulk', data),
  listAttendance: (params) => api.get('/hr/attendance', { params }),
};

// AMC API
export const amcAPI = {
  list: (params) => api.get('/amc', { params }),
  create: (data) => api.post('/amc', data),
  update: (id, data) => api.patch(`/amc/${id}`, data),
  renew: (id) => api.post(`/amc/${id}/renew`),
  cancel: (id) => api.post(`/amc/${id}/cancel`),
  expiring: (params) => api.get('/amc/expiring', { params }),
  stats: () => api.get('/amc/stats'),
};

// Analytics API
export const analyticsAPI = {
  businessHealth: () => api.get('/analytics/business-health'),
  deadStock: (params) => api.get('/analytics/dead-stock', { params }),
  movingItems: (params) => api.get('/analytics/moving-items', { params }),
  profitPerProduct: (params) => api.get('/analytics/profit-per-product', { params }),
};

// Delivery Notes API
export const deliveryNoteAPI = {
  list: (params) => api.get('/delivery-notes', { params }),
  create: (data) => api.post('/delivery-notes', data),
  get: (id) => api.get(`/delivery-notes/${id}`),
  update: (id, data) => api.put(`/delivery-notes/${id}`, data),
  delete: (id) => api.delete(`/delivery-notes/${id}`),
};

// Masters API (Units & Brands)
export const mastersAPI = {
  listUnits: () => api.get('/masters/units'),
  createUnit: (data) => api.post('/masters/units', data),
  updateUnit: (id, data) => api.put(`/masters/units/${id}`, data),
  deleteUnit: (id) => api.delete(`/masters/units/${id}`),
  listBrands: () => api.get('/masters/brands'),
  createBrand: (data) => api.post('/masters/brands', data),
  updateBrand: (id, data) => api.put(`/masters/brands/${id}`, data),
  deleteBrand: (id) => api.delete(`/masters/brands/${id}`),
  listCategories: () => api.get('/masters/categories'),
  createCategory: (data) => api.post('/masters/categories', data),
  updateCategory: (id, data) => api.put(`/masters/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/masters/categories/${id}`),
  listSubcategories: (category_name) => api.get('/masters/subcategories', { params: category_name ? { category_name } : {} }),
  createSubcategory: (data) => api.post('/masters/subcategories', data),
  updateSubcategory: (id, data) => api.put(`/masters/subcategories/${id}`, data),
  deleteSubcategory: (id) => api.delete(`/masters/subcategories/${id}`),
  syncFromProducts: () => api.post('/masters/sync-from-products'),
};

// Indian Locale API
export const localeAPI = {
  amountInWords: (amount) => api.get('/locale/amount-in-words', { params: { amount } }),
  financialYear: () => api.get('/locale/financial-year'),
  holidays: (params) => api.get('/locale/holidays', { params }),
  translations: (lang) => api.get(`/locale/translations/${lang}`),
  stateCodes: () => api.get('/locale/state-codes'),
};

// Global Search API
export const searchAPI = {
  global: (q) => api.get('/search', { params: { q } }),
};

export default api;
